import { Download, ExternalLink, Filter, RotateCw, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { clearPlacement, getScans, postersToCsv, resetPosterScans, scansToCsv, updatePoster } from "../lib/api";
import { type DesignType, type Poster, type PosterUpdatePayload, type ScanEvent } from "../lib/types";
import { MetricCard } from "./MetricCard";
import { PosterMap } from "./PosterMap";

interface DashboardProps {
  posters: Poster[];
  onRefresh: () => Promise<void>;
}

const ALL = "All";

type DetailTab = "overview" | "logs" | "edit";

function dayKey(value: string) {
  return value.slice(0, 10);
}

function hourKey(value: string) {
  return `${new Date(value).getHours().toString().padStart(2, "0")}:00`;
}

function browserLabel(userAgent?: string) {
  if (!userAgent) return "Unknown";
  const device = /iPhone|iPad|iPod/i.test(userAgent) ? "iOS" : /Android/i.test(userAgent) ? "Android" : /Mac|Windows|Linux/i.test(userAgent) ? "Desktop" : "Unknown";
  const browser = /CriOS|Chrome/i.test(userAgent) ? "Chrome" : /Safari/i.test(userAgent) ? "Safari" : /Firefox/i.test(userAgent) ? "Firefox" : "Browser";
  return `${device} ${browser}`;
}

function downloadCsv(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function groupRows<T>(items: T[], key: (item: T) => string, value: (item: T) => number = () => 1) {
  const rows = new Map<string, number>();
  items.forEach((item) => rows.set(key(item), (rows.get(key(item)) ?? 0) + value(item)));
  return Array.from(rows.entries())
    .map(([name, scans]) => ({ name, scans }))
    .sort((a, b) => b.scans - a.scans);
}

export function Dashboard({ posters, onRefresh }: DashboardProps) {
  const [scans, setScans] = useState<ScanEvent[]>([]);
  const [scanError, setScanError] = useState<string | null>(null);
  const [design, setDesign] = useState<DesignType | typeof ALL>(ALL);
  const [status, setStatus] = useState<"all" | "placed" | "unplaced">("all");
  const [posterSearch, setPosterSearch] = useState("");
  const [neighborhood, setNeighborhood] = useState<typeof ALL | string>(ALL);
  const [venueType, setVenueType] = useState<typeof ALL | string>(ALL);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [minScans, setMinScans] = useState(0);
  const [selectedPoster, setSelectedPoster] = useState<Poster | undefined>(posters[0]);
  const [detailTab, setDetailTab] = useState<DetailTab>("overview");
  const [editDraft, setEditDraft] = useState<PosterUpdatePayload>({});
  const [actionError, setActionError] = useState<string | null>(null);

  async function refreshAll() {
    await onRefresh();
    try {
      setScanError(null);
      setScans(await getScans());
    } catch (error) {
      setScanError(error instanceof Error ? error.message : "Unable to load scan logs");
    }
  }

  useEffect(() => {
    void refreshAll();
  }, []);

  useEffect(() => {
    if (!selectedPoster && posters.length > 0) {
      setSelectedPoster(posters[0]);
      return;
    }
    if (selectedPoster && posters.every((poster) => poster.posterId !== selectedPoster.posterId)) {
      setSelectedPoster(posters[0]);
      return;
    }
    if (selectedPoster) {
      const updated = posters.find((poster) => poster.posterId === selectedPoster.posterId);
      if (updated && updated !== selectedPoster) setSelectedPoster(updated);
    }
  }, [posters, selectedPoster]);

  useEffect(() => {
    if (!selectedPoster) return;
    setEditDraft({
      designType: selectedPoster.designType,
      batchId: selectedPoster.batchId,
      status: selectedPoster.status,
      notes: selectedPoster.notes ?? "",
      photoUrl: selectedPoster.photoUrl ?? "",
      placementLat: selectedPoster.placementLat ?? null,
      placementLng: selectedPoster.placementLng ?? null,
      placementAccuracy: selectedPoster.placementAccuracy ?? null,
      placementTimestamp: selectedPoster.placementTimestamp ?? null,
      tags: {
        neighborhood: selectedPoster.tags?.neighborhood ?? "",
        venue: selectedPoster.tags?.venue ?? "",
        venueType: selectedPoster.tags?.venueType ?? "",
        visibilityRating: selectedPoster.tags?.visibilityRating ?? 4,
        facingDirection: selectedPoster.tags?.facingDirection ?? ""
      }
    });
  }, [selectedPoster?.posterId]);

  const designOptions = useMemo(() => Array.from(new Set(posters.map((poster) => poster.designType))).sort(), [posters]);
  const neighborhoodOptions = useMemo(
    () => Array.from(new Set(posters.map((poster) => poster.tags?.neighborhood).filter(Boolean) as string[])).sort(),
    [posters]
  );
  const venueTypeOptions = useMemo(
    () => Array.from(new Set(posters.map((poster) => poster.tags?.venueType).filter(Boolean) as string[])).sort(),
    [posters]
  );
  const posterById = useMemo(() => new Map(posters.map((poster) => [poster.posterId, poster])), [posters]);

  const filteredPosters = useMemo(() => {
    const search = posterSearch.trim().toUpperCase();
    return posters.filter((poster) => {
      if (design !== ALL && poster.designType !== design) return false;
      if (status === "placed" && !poster.isPlaced) return false;
      if (status === "unplaced" && poster.isPlaced) return false;
      if (neighborhood !== ALL && poster.tags?.neighborhood !== neighborhood) return false;
      if (venueType !== ALL && poster.tags?.venueType !== venueType) return false;
      if (search && !poster.posterId.toUpperCase().includes(search)) return false;
      if (poster.scanCount < minScans) return false;
      return true;
    });
  }, [posters, design, status, neighborhood, venueType, posterSearch, minScans]);

  const filteredPosterIds = useMemo(() => new Set(filteredPosters.map((poster) => poster.posterId)), [filteredPosters]);

  const filteredScans = useMemo(() => {
    return scans.filter((scan) => {
      if (!filteredPosterIds.has(scan.posterId)) return false;
      if (dateFrom && dayKey(scan.scannedAt) < dateFrom) return false;
      if (dateTo && dayKey(scan.scannedAt) > dateTo) return false;
      return true;
    });
  }, [scans, filteredPosterIds, dateFrom, dateTo]);

  const activeScans = filteredScans.filter((scan) => !scan.ignoredForAnalytics);
  const selectedPosterScans = filteredScans.filter((scan) => scan.posterId === selectedPoster?.posterId);

  const stats = useMemo(() => {
    const placed = filteredPosters.filter((poster) => poster.isPlaced).length;
    const byPoster = groupRows(activeScans, (scan) => scan.posterId);
    const topPoster = byPoster[0]?.name ?? "None yet";
    const avgPlaced = placed ? (activeScans.length / placed).toFixed(1) : "0.0";
    return { placed, topPoster, avgPlaced };
  }, [filteredPosters, activeScans]);

  const scanTrendRows = useMemo(
    () => groupRows(activeScans, (scan) => dayKey(scan.scannedAt)).sort((a, b) => a.name.localeCompare(b.name)),
    [activeScans]
  );
  const designRows = useMemo(() => groupRows(activeScans, (scan) => scan.designType ?? posterById.get(scan.posterId)?.designType ?? "Unknown"), [activeScans, posterById]);
  const neighborhoodRows = useMemo(() => groupRows(activeScans, (scan) => posterById.get(scan.posterId)?.tags?.neighborhood || "Unlabeled"), [activeScans, posterById]);
  const venueRows = useMemo(() => groupRows(activeScans, (scan) => posterById.get(scan.posterId)?.tags?.venueType || "Unlabeled"), [activeScans, posterById]);
  const hourRows = useMemo(
    () => groupRows(activeScans, (scan) => hourKey(scan.scannedAt)).sort((a, b) => a.name.localeCompare(b.name)),
    [activeScans]
  );
  const topPosterRows = useMemo(() => groupRows(activeScans, (scan) => scan.posterId).slice(0, 10), [activeScans]);

  async function clearSelectedPlacement() {
    if (!selectedPoster?.isPlaced) return;
    const confirmed = window.confirm(`Clear placement data for ${selectedPoster.posterId}? This keeps the poster and scan history.`);
    if (!confirmed) return;
    await clearPlacement(selectedPoster.posterId);
    await refreshAll();
  }

  async function savePosterEdits() {
    if (!selectedPoster) return;
    setActionError(null);
    try {
      await updatePoster(selectedPoster.posterId, editDraft);
      await refreshAll();
      setDetailTab("overview");
    } catch (error) {
      setActionError(error instanceof Error ? error.message : "Unable to update poster");
    }
  }

  async function resetSelectedScans() {
    if (!selectedPoster) return;
    const confirmed = window.confirm(`Reset scan count for ${selectedPoster.posterId}? Existing logs will be archived for analytics, not deleted.`);
    if (!confirmed) return;
    await resetPosterScans(selectedPoster.posterId);
    await refreshAll();
  }

  return (
    <main className="dashboard-layout dashboard-admin">
      <section className="dashboard-toolbar">
        <div className="section-heading">
          <h1>Poster Analytics</h1>
          <p>Inspect scan logs, compare designs and locations, and manage every printed poster.</p>
        </div>
        <div className="toolbar-actions">
          <button className="secondary-button" onClick={() => void refreshAll()}>
            <RotateCw size={16} /> Refresh
          </button>
          <button className="secondary-button" onClick={() => downloadCsv(`posterly-posters-${new Date().toISOString().slice(0, 10)}.csv`, postersToCsv(filteredPosters))}>
            <Download size={16} /> Posters CSV
          </button>
          <button className="secondary-button" onClick={() => downloadCsv(`posterly-scans-${new Date().toISOString().slice(0, 10)}.csv`, scansToCsv(filteredScans))}>
            <Download size={16} /> Scans CSV
          </button>
        </div>
      </section>

      <section className="filter-panel">
        <label>
          <Filter size={16} /> Design
          <select value={design} onChange={(event) => setDesign(event.target.value)}>
            <option>{ALL}</option>
            {designOptions.map((item) => <option key={item}>{item}</option>)}
          </select>
        </label>
        <label>
          Status
          <select value={status} onChange={(event) => setStatus(event.target.value as "all" | "placed" | "unplaced")}>
            <option value="all">All</option>
            <option value="placed">Placed</option>
            <option value="unplaced">Unplaced</option>
          </select>
        </label>
        <label>
          Poster
          <input value={posterSearch} onChange={(event) => setPosterSearch(event.target.value)} placeholder="MN-001" />
        </label>
        <label>
          Neighborhood
          <select value={neighborhood} onChange={(event) => setNeighborhood(event.target.value)}>
            <option>{ALL}</option>
            {neighborhoodOptions.map((item) => <option key={item}>{item}</option>)}
          </select>
        </label>
        <label>
          Venue type
          <select value={venueType} onChange={(event) => setVenueType(event.target.value)}>
            <option>{ALL}</option>
            {venueTypeOptions.map((item) => <option key={item}>{item}</option>)}
          </select>
        </label>
        <label>
          From
          <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
        </label>
        <label>
          To
          <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
        </label>
        <label>
          Min scans
          <input type="number" min="0" value={minScans} onChange={(event) => setMinScans(Number(event.target.value))} />
        </label>
      </section>

      {scanError ? <div className="error-box">{scanError}</div> : null}
      {actionError ? <div className="error-box">{actionError}</div> : null}

      <section className="metric-grid">
        <MetricCard label="Filtered posters" value={filteredPosters.length} detail={`${stats.placed} placed`} />
        <MetricCard label="Active scans" value={activeScans.length} detail={`${filteredScans.length - activeScans.length} archived`} />
        <MetricCard label="Avg per placed" value={stats.avgPlaced} detail="Filtered active scans" />
        <MetricCard label="Top poster" value={stats.topPoster} detail="By active scans" />
      </section>

      <section className="dashboard-main">
        <div className="map-panel">
          <PosterMap posters={filteredPosters} selectedPoster={selectedPoster} onSelect={(poster) => { setSelectedPoster(poster); setDetailTab("overview"); }} />
        </div>
        <aside className="detail-panel poster-detail">
          {selectedPoster ? (
            <>
              <div>
                <span className="eyebrow">{selectedPoster.designType}</span>
                <h2>{selectedPoster.posterId}</h2>
                <p>{selectedPoster.isPlaced ? "Placed" : "Not placed"} · {selectedPoster.scanCount} stored scans · {selectedPosterScans.filter((scan) => !scan.ignoredForAnalytics).length} active logs</p>
              </div>
              <div className="tab-row">
                {(["overview", "logs", "edit"] as DetailTab[]).map((tab) => (
                  <button key={tab} className={detailTab === tab ? "active" : ""} onClick={() => setDetailTab(tab)}>{tab}</button>
                ))}
              </div>

              {detailTab === "overview" ? (
                <>
                  <dl>
                    <div><dt>Batch</dt><dd>{selectedPoster.batchId}</dd></div>
                    <div><dt>Location</dt><dd>{selectedPoster.tags?.venue || "Unlabeled"}{selectedPoster.tags?.neighborhood ? `, ${selectedPoster.tags.neighborhood}` : ""}</dd></div>
                    <div><dt>Last scan</dt><dd>{selectedPoster.lastScanAt ? new Date(selectedPoster.lastScanAt).toLocaleString() : "None yet"}</dd></div>
                    <div><dt>Placement</dt><dd>{selectedPoster.placementTimestamp ? new Date(selectedPoster.placementTimestamp).toLocaleString() : "Not placed"}</dd></div>
                    <div><dt>Notes</dt><dd>{selectedPoster.notes || "No notes"}</dd></div>
                  </dl>
                  {selectedPoster.photoUrl ? <img className="placement-photo" src={selectedPoster.photoUrl} alt="" /> : null}
                  <a className="primary-button" href={selectedPoster.qrUrl} target="_blank" rel="noreferrer"><ExternalLink size={16} /> Simulate scan</a>
                  <button className="danger-button" onClick={() => void resetSelectedScans()}>Reset scan count</button>
                  {selectedPoster.isPlaced ? <button className="danger-button" onClick={() => void clearSelectedPlacement()}>Clear placement</button> : null}
                </>
              ) : null}

              {detailTab === "logs" ? (
                <ScanTable scans={selectedPosterScans} posters={posterById} compact />
              ) : null}

              {detailTab === "edit" ? (
                <div className="edit-grid">
                  <label>Design<input value={editDraft.designType ?? ""} onChange={(event) => setEditDraft((draft) => ({ ...draft, designType: event.target.value }))} /></label>
                  <label>Batch<input value={editDraft.batchId ?? ""} onChange={(event) => setEditDraft((draft) => ({ ...draft, batchId: event.target.value }))} /></label>
                  <label>Status<select value={editDraft.status ?? "unplaced"} onChange={(event) => setEditDraft((draft) => ({ ...draft, status: event.target.value as Poster["status"] }))}><option value="unplaced">Unplaced</option><option value="placed">Placed</option><option value="retired">Retired</option></select></label>
                  <label>Neighborhood<input value={editDraft.tags?.neighborhood ?? ""} onChange={(event) => setEditDraft((draft) => ({ ...draft, tags: { ...draft.tags, neighborhood: event.target.value } }))} /></label>
                  <label>Venue type<input value={editDraft.tags?.venueType ?? ""} onChange={(event) => setEditDraft((draft) => ({ ...draft, tags: { ...draft.tags, venueType: event.target.value } }))} /></label>
                  <label>Venue<input value={editDraft.tags?.venue ?? ""} onChange={(event) => setEditDraft((draft) => ({ ...draft, tags: { ...draft.tags, venue: event.target.value } }))} /></label>
                  <label>Lat<input type="number" value={editDraft.placementLat ?? ""} onChange={(event) => setEditDraft((draft) => ({ ...draft, placementLat: event.target.value ? Number(event.target.value) : null }))} /></label>
                  <label>Lng<input type="number" value={editDraft.placementLng ?? ""} onChange={(event) => setEditDraft((draft) => ({ ...draft, placementLng: event.target.value ? Number(event.target.value) : null }))} /></label>
                  <label className="full-span">Notes<textarea value={editDraft.notes ?? ""} onChange={(event) => setEditDraft((draft) => ({ ...draft, notes: event.target.value }))} /></label>
                  <button className="primary-button full-span" onClick={() => void savePosterEdits()}><Save size={16} /> Save poster info</button>
                </div>
              ) : null}
            </>
          ) : <p>Select a poster pin or table row.</p>}
        </aside>
      </section>

      <section className="analytics-grid analytics-grid-three">
        <ChartPanel title="Scans Over Time" data={scanTrendRows} type="line" />
        <ChartPanel title="By Design" data={designRows} />
        <ChartPanel title="By Neighborhood" data={neighborhoodRows} />
        <ChartPanel title="By Venue Type" data={venueRows} />
        <ChartPanel title="By Hour" data={hourRows} type="line" />
        <ChartPanel title="Top Posters" data={topPosterRows} />
      </section>

      <section className="table-panel">
        <h2>Scan Logs</h2>
        <ScanTable scans={filteredScans} posters={posterById} />
      </section>

      <section className="table-panel">
        <h2>Poster Records</h2>
        <div className="table-scroll">
          <table>
            <thead>
              <tr><th>Poster</th><th>Design</th><th>Status</th><th>Location</th><th>Scans</th><th>Last scan</th></tr>
            </thead>
            <tbody>
              {filteredPosters.map((poster) => (
                <tr key={poster.posterId} onClick={() => { setSelectedPoster(poster); setDetailTab("overview"); }}>
                  <td>{poster.posterId}</td><td>{poster.designType}</td><td>{poster.isPlaced ? "Placed" : "Unplaced"}</td>
                  <td>{poster.tags?.venue || poster.tags?.neighborhood || "None"}</td><td>{poster.scanCount}</td>
                  <td>{poster.lastScanAt ? new Date(poster.lastScanAt).toLocaleDateString() : "None"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}

function ChartPanel({ title, data, type = "bar" }: { title: string; data: { name: string; scans: number }[]; type?: "bar" | "line" }) {
  return (
    <article className="chart-panel">
      <h2>{title}</h2>
      <ResponsiveContainer width="100%" height={250}>
        {type === "line" ? (
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} /><Tooltip />
            <Line type="monotone" dataKey="scans" stroke="#2563eb" strokeWidth={3} dot />
          </LineChart>
        ) : (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} /><Tooltip />
            <Bar dataKey="scans" fill="#111827" radius={[4, 4, 0, 0]} />
          </BarChart>
        )}
      </ResponsiveContainer>
    </article>
  );
}

function ScanTable({ scans, posters, compact = false }: { scans: ScanEvent[]; posters: Map<string, Poster>; compact?: boolean }) {
  const visible = compact ? scans.slice(0, 30) : scans.slice(0, 250);
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr><th>Time</th><th>Poster</th><th>Design</th><th>Location</th><th>Device</th><th>Referrer</th><th>Status</th></tr>
        </thead>
        <tbody>
          {visible.map((scan) => {
            const poster = posters.get(scan.posterId);
            return (
              <tr key={scan.id}>
                <td>{new Date(scan.scannedAt).toLocaleString()}</td>
                <td>{scan.posterId}</td>
                <td>{scan.designType ?? poster?.designType ?? "Unknown"}</td>
                <td>{poster?.tags?.venue || poster?.tags?.neighborhood || "None"}</td>
                <td>{browserLabel(scan.userAgent)}</td>
                <td>{scan.referrer || "Direct"}</td>
                <td>{scan.ignoredForAnalytics ? "Archived" : "Active"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {scans.length > visible.length ? <p className="table-note">Showing {visible.length} of {scans.length} logs. Export CSV for the full filtered set.</p> : null}
    </div>
  );
}
